import { useEffect, useState } from 'react'

/**
 * Times as a person in the workshop reads and types them — in the phone's own
 * zone, which is the workshop's. The *report* is computed on the server in the
 * configured zone (`TIME_ZONE`); these are only for showing and typing.
 */

/** `8:02 am`. */
export function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

const pad = (n: number): string => String(n).padStart(2, '0')

/** The value an `<input type="datetime-local">` wants: `2026-10-21T16:30`, local. */
export function toLocalInput(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Back from the input to an instant. Null for an empty or half-typed field. */
export function fromLocalInput(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return null
  const t = new Date(value)
  return Number.isNaN(t.getTime()) ? null : t.toISOString()
}

/**
 * "When did you leave?" answered with a time alone, on the day the shift
 * started — or the next day, if that time is before the start (a shift that
 * ran past midnight). Typing a date as well is the step people get wrong.
 */
export function endOnShiftDay(startIso: string, time: string): string | null {
  const m = /^(\d{2}):(\d{2})$/.exec(time)
  if (!m) return null
  const end = new Date(startIso)
  end.setHours(Number(m[1]), Number(m[2]), 0, 0)
  if (end.getTime() <= Date.parse(startIso)) end.setDate(end.getDate() + 1)
  return end.toISOString()
}

/** The current time, re-read every `everyMs`, for a clock that counts up. */
export function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs)
    return () => clearInterval(id)
  }, [everyMs])
  return now
}

/** A server error's sentence, without the status prefix `HttpError` adds. */
export function problemOf(e: unknown): string {
  return String(e instanceof Error ? e.message : e).replace(/^\d{3}:\s*/, '')
}

/** Today's date on this device, `2026-10-22` — only for where to open a calendar before the server has said. */
export function deviceToday(at: Date = new Date()): string {
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
}
